import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Chip, Stack,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import DownloadIcon from '@mui/icons-material/Download';
import { starAPI } from '../../api';
import { useToast } from '../../components/common/Toast';
import { downloadCsv } from '../../utils/exportCsv';

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  return new Date(parseInt(y, 10), parseInt(mo, 10) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
};
const TYPE_LABEL = { project: 'Project', defect: 'Defect', task: 'Task' };

export default function StarsReportPage() {
  const { showToast } = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState({ totals: [], details: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!month) return;
    setLoading(true);
    starAPI.summary(month)
      .then((res) => setData(res.data))
      .catch(() => showToast('Failed to load stars report', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const downloadSummary = () => {
    if (!data.totals.length) return showToast('No stars in this month', 'error');
    downloadCsv(`stars-summary-${month}.csv`,
      ['Rank', 'Name', 'Role', 'Projects', 'Defects', 'Tasks', 'Total Stars'],
      data.totals.map((t, i) => [i + 1, t.name, (t.role || '').replace('_', ' '), t.project, t.defect, t.task, t.stars]));
  };

  const downloadDetails = () => {
    if (!data.details.length) return showToast('No stars in this month', 'error');
    downloadCsv(`stars-detail-${month}.csv`,
      ['Name', 'Type', 'Item', 'TAT Date', 'Completed On', 'Final Status', 'Points'],
      data.details.map((d) => [d.name, TYPE_LABEL[d.entity_type] || d.entity_type, d.entity_name || '', d.tat_date, d.completed_on, d.final_status || '', d.points]));
  };

  const grand = data.totals.reduce((n, t) => n + t.stars, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>Stars Report</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
            +1 star each time an assigned item is completed or cancelled on or before its TAT date
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <TextField type="month" size="small" label="Month" value={month} InputLabelProps={{ shrink: true }}
            onChange={(e) => setMonth(e.target.value)} />
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadSummary}>Summary CSV</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadDetails}>Detailed CSV</Button>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StarIcon sx={{ color: '#F9A825' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{monthLabel(month)} — {grand} star{grand === 1 ? '' : 's'}</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#F4F7FB', fontWeight: 700, color: '#1E3A5F', fontSize: '0.75rem' } }}>
                      <TableCell width={50}>Rank</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="center">Projects</TableCell>
                      <TableCell align="center">Defects</TableCell>
                      <TableCell align="center">Tasks</TableCell>
                      <TableCell align="center">Stars</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.totals.map((t, i) => (
                      <TableRow key={t.user_id} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{(t.role || '').replace('_', ' ')}</TableCell>
                        <TableCell align="center">{t.project}</TableCell>
                        <TableCell align="center">{t.defect}</TableCell>
                        <TableCell align="center">{t.task}</TableCell>
                        <TableCell align="center">
                          <Chip icon={<StarIcon sx={{ fontSize: 16, color: '#F9A825 !important' }} />} label={t.stars} size="small" sx={{ fontWeight: 800 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.totals.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No stars awarded in this month yet.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Details</Typography>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#F4F7FB', fontWeight: 700, color: '#1E3A5F', fontSize: '0.75rem' } }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>TAT Date</TableCell>
                      <TableCell>Completed On</TableCell>
                      <TableCell>Final Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.details.map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{d.name}</TableCell>
                        <TableCell>{TYPE_LABEL[d.entity_type] || d.entity_type}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}><Typography variant="body2" noWrap>{d.entity_name}</Typography></TableCell>
                        <TableCell>{d.tat_date}</TableCell>
                        <TableCell>{d.completed_on}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{(d.final_status || '').replace('_', ' ')}</TableCell>
                      </TableRow>
                    ))}
                    {data.details.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nothing to show.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
